import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Crown, Lock, AlertTriangle, Calendar, Sparkles } from 'lucide-react';

/**
 * Modal shown when trial users hit limits or try blocked features
 * Types: 'trial_expired', 'trial_project_limit', 'trial_comparison_limit', 'trial_export_blocked'
 */
export function TrialUpgradeModal({ 
  isOpen, 
  onClose, 
  type = 'trial_expired',
  onUpgrade 
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    onClose();
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/subscription');
    }
  };

  const handleViewPlans = () => {
    onClose();
    navigate('/subscription');
  };

  // Configuration per type
  const config = {
    trial_expired: {
      icon: AlertTriangle,
      iconColor: 'text-amber-500',
      iconBg: 'bg-amber-100',
      title: t('trial_expired_title'),
      message: t('trial_expired_message'),
      showViewPlans: true
    },
    trial_project_limit: {
      icon: Lock,
      iconColor: 'text-blue-500',
      iconBg: 'bg-blue-100',
      title: t('trial_project_limit_title'),
      message: t('trial_project_limit_message'),
      showViewPlans: true
    },
    trial_comparison_limit: {
      icon: Lock,
      iconColor: 'text-purple-500',
      iconBg: 'bg-purple-100',
      title: t('trial_comparison_limit_title'),
      message: t('trial_comparison_limit_message'),
      showViewPlans: true
    },
    trial_export_blocked: {
      icon: Lock,
      iconColor: 'text-rose-500',
      iconBg: 'bg-rose-100',
      title: t('trial_export_blocked_title'),
      message: t('trial_export_blocked_message'),
      showViewPlans: true
    }
  };

  const currentConfig = config[type] || config.trial_expired;
  const Icon = currentConfig.icon;

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4" data-testid="trial-upgrade-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 p-6 text-white text-center">
          <div className={`w-16 h-16 mx-auto mb-4 ${currentConfig.iconBg} rounded-full flex items-center justify-center`}>
            <Icon className={`w-8 h-8 ${currentConfig.iconColor}`} />
          </div>
          <h2 className="text-xl font-bold">{currentConfig.title}</h2>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <p className="text-slate-600 text-center mb-6">
            {currentConfig.message}
          </p>
          
          {/* Features reminder */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-500" />
              {t('subscription') || 'Subscription'} includes:
            </p>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-indigo-500" />
                {t('unlimited_markers') || 'Unlimited markers'}
              </li>
              <li className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-indigo-500" />
                {t('unlimited_ai_credits') || 'Unlimited AI credits'}
              </li>
              <li className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-indigo-500" />
                {t('full_export') || 'Full PDF & Excel export'}
              </li>
            </ul>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-3">
            {currentConfig.showViewPlans && (
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={handleViewPlans}
                data-testid="trial-view-plans-btn"
              >
                {t('view_plans')}
              </Button>
            )}
            <Button 
              className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
              onClick={handleUpgrade}
              data-testid="trial-upgrade-btn"
            >
              <Crown className="w-4 h-4 mr-2" />
              {t('upgrade_now')}
            </Button>
          </div>
          
          {/* Close button */}
          <button
            onClick={onClose}
            className="w-full mt-4 text-sm text-slate-500 hover:text-slate-700"
            data-testid="trial-close-btn"
          >
            {t('cancel') || 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TrialUpgradeModal;
